import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import {
  COMPANY_NAME,
  SITE_DOMAIN,
  SITE_NAME,
  SITE_URL,
} from '../constants/site';

const UPDATED = 'May 24, 2026';

export default function PrivacyPolicyPage() {
  return (
    <PolicyLayout title="Privacy Policy" lastUpdated={UPDATED}>
      <PolicySection title="1. Introduction">
        <p>
          Welcome to {SITE_NAME} ({SITE_URL}, &ldquo;{SITE_DOMAIN}&rdquo;, &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;). This Privacy Policy describes how {COMPANY_NAME} collects, uses, discloses, and safeguards
          personal information when you visit our website, use our mobile-friendly services, search for travel, subscribe
          to communications, or complete a booking.
        </p>
        <p>
          By using our services, you acknowledge that you have read this Privacy Policy. If you do not agree with our
          practices, please do not use {SITE_DOMAIN}. For information about cookies and similar technologies, see our{' '}
          <Link to="/flight/cookies">Cookie Policy</Link>. For terms governing bookings and use of the site, see our{' '}
          <Link to="/flight/terms">Terms &amp; Conditions</Link>.
        </p>
      </PolicySection>

      <PolicySection title="2. Who we are">
        <p>
          {SITE_NAME} is a travel booking platform that helps consumers compare and book flights, hotels, rental cars,
          vacation packages, and cruises. {COMPANY_NAME} operates {SITE_DOMAIN} and acts as an intermediary between you
          and third-party travel suppliers. We are the data controller for personal information processed through our
          website and customer support channels, except where a supplier processes data under its own privacy policy
          after a booking is completed.
        </p>
      </PolicySection>

      <PolicySection title="3. Information we collect">
        <PolicySubsection title="3.1 Information you provide directly">
          <PolicyList
            items={[
              'Identity and contact data: full name, email address, telephone number, billing address, and emergency contact details.',
              'Booking data: passenger names (as on government ID), dates of birth, gender (where required by airlines), loyalty program numbers, seat and meal preferences, hotel room requirements, and driver information for car rentals.',
              'Payment data: card type, partial card digits, billing ZIP/postal code, and transaction identifiers. Full payment card numbers are processed by our PCI-compliant payment partners; we do not store complete card numbers on our servers.',
              'Communications: content of emails, chat messages, and phone calls with our support team, including recordings where permitted by law and disclosed at the time of the call.',
              'Marketing preferences: newsletter sign-ups, survey responses, and opt-in/opt-out choices.',
            ]}
          />
        </PolicySubsection>
        <PolicySubsection title="3.2 Information collected automatically">
          <PolicyList
            items={[
              'Device and browser data: IP address, browser type and version, operating system, device identifiers, screen resolution, and language settings.',
              'Usage data: pages viewed, search queries (origin, destination, dates), click paths, referral URLs, time spent on pages, and error logs.',
              'Location data: approximate location derived from IP address; precise location only if you enable location services on your device.',
              'Cookies and similar technologies: as described in our Cookie Policy.',
            ]}
          />
        </PolicySubsection>
        <PolicySubsection title="3.3 Information from third parties">
          <p>We may receive information from:</p>
          <PolicyList
            items={[
              'Travel suppliers (airlines, hotels, car rental companies, cruise lines) regarding booking status, schedule changes, and refunds.',
              'Payment processors and fraud-prevention services to verify transactions.',
              'Advertising and analytics partners (where you have consented to marketing or analytics cookies).',
              'Publicly available sources and identity-verification providers where required for high-risk transactions.',
            ]}
          />
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="4. How we use your information">
        <p>We use personal information for the following purposes:</p>
        <PolicyList
          items={[
            'Performing our contract with you: processing searches, completing bookings, issuing confirmations, and providing post-booking support.',
            'Customer service: responding to inquiries, resolving complaints, and managing changes or cancellations.',
            'Payments and fraud prevention: authorizing charges, detecting fraudulent activity, and complying with financial regulations.',
            'Legal compliance: tax reporting, responding to lawful requests from authorities, and enforcing our Terms & Conditions.',
            'Legitimate interests: improving our website, conducting analytics, training staff, and protecting our rights—balanced against your privacy rights.',
            'Marketing: sending promotional emails and offers where you have opted in; you may unsubscribe at any time via the link in each email or by contacting us.',
          ]}
        />
      </PolicySection>

      <PolicySection title="5. Legal bases for processing (EEA/UK users)">
        <p>If you are located in the European Economic Area or United Kingdom, we process personal data under these legal bases:</p>
        <PolicyList
          items={[
            'Contract performance — to complete bookings and provide travel services you request.',
            'Legitimate interests — site security, fraud prevention, analytics, and service improvement.',
            'Consent — marketing communications and non-essential cookies.',
            'Legal obligation — compliance with tax, aviation security, and consumer protection laws.',
          ]}
        />
      </PolicySection>

      <PolicySection title="6. How we share your information">
        <p>We may share personal information with:</p>
        <PolicyList
          items={[
            'Travel suppliers to fulfill your reservation (airlines, hotels, GDS providers, car rental brands, cruise operators, and tour operators).',
            'Payment processors and banks to complete transactions.',
            'Technology vendors hosting our website, email systems, CRM tools, and customer support platforms under contractual confidentiality obligations.',
            'Professional advisers (lawyers, accountants, insurers) when necessary.',
            'Law enforcement or regulators when required by applicable law or court order.',
            'Successors in the event of a merger, acquisition, or sale of assets, subject to this Privacy Policy.',
          ]}
        />
        <p>
          We do not sell your personal information to third parties for their independent marketing purposes. We may share
          aggregated or de-identified data that cannot reasonably identify you.
        </p>
      </PolicySection>

      <PolicySection title="7. International data transfers">
        <p>
          {COMPANY_NAME} is based in the United States. If you access our services from outside the U.S., your information
          may be transferred to, stored in, and processed in the United States and other countries where our service
          providers operate. These countries may have data protection laws different from your country. Where required,
          we implement appropriate safeguards such as standard contractual clauses approved by relevant authorities.
        </p>
      </PolicySection>

      <PolicySection title="8. Data retention">
        <p>We retain personal information only as long as necessary for the purposes described in this policy:</p>
        <PolicyList
          items={[
            'Booking records: typically seven (7) years after travel completion for tax, chargeback, and regulatory purposes.',
            'Marketing data: until you unsubscribe or request deletion, plus a short suppression period to honor opt-out requests.',
            'Support tickets: up to three (3) years after resolution unless a longer period is required for disputes.',
            'Server logs and analytics: generally twelve (12) to twenty-four (24) months, unless needed for security investigations.',
          ]}
        />
      </PolicySection>

      <PolicySection title="9. Security">
        <p>
          We implement administrative, technical, and physical safeguards designed to protect personal information,
          including encryption in transit (TLS/SSL), access controls, and secure payment processing. No method of
          transmission or storage is 100% secure. You are responsible for maintaining the confidentiality of any account
          credentials and for notifying us promptly of suspected unauthorized access.
        </p>
      </PolicySection>

      <PolicySection title="10. Your privacy rights">
        <PolicySubsection title="10.1 General rights">
          <p>Depending on your location, you may have the right to:</p>
          <PolicyList
            items={[
              'Access a copy of the personal information we hold about you.',
              'Correct inaccurate or incomplete information.',
              'Delete your information, subject to legal retention requirements.',
              'Restrict or object to certain processing activities.',
              'Receive your data in a portable format where technically feasible.',
              'Withdraw consent for marketing or cookies at any time without affecting prior lawful processing.',
            ]}
          />
        </PolicySubsection>
        <PolicySubsection title="10.2 California residents (CCPA/CPRA)">
          <p>
            California residents may have additional rights under the California Consumer Privacy Act, including the
            right to know categories of personal information collected, request deletion, and opt out of the
            &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal information. We do not sell personal information as
            defined under California law. To submit a request, email us at the address below. We will verify your identity
            before responding and will not discriminate against you for exercising your rights.
          </p>
        </PolicySubsection>
        <PolicySubsection title="10.3 How to exercise your rights">
          <p>
            To submit a privacy request, contact us using the details in Section 14. We will respond within the timeframe
            required by applicable law (typically 30–45 days). You may also lodge a complaint with your local data
            protection authority if you believe our processing violates applicable law.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="11. Children’s privacy">
        <p>
          Our services are not directed to individuals under sixteen (16) years of age. We do not knowingly collect
          personal information from children. If you believe we have collected information from a child, please contact
          us and we will delete it promptly.
        </p>
      </PolicySection>

      <PolicySection title="12. Third-party websites and links">
        <p>
          Our website may contain links to airline sites, hotel partners, and other third parties. We are not
          responsible for the privacy practices of those websites. We encourage you to review their privacy policies
          before providing personal information.
        </p>
      </PolicySection>

      <PolicySection title="13. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date at the top indicates
          when changes were last made. Material changes will be posted on this page; continued use of {SITE_DOMAIN} after
          changes constitutes acceptance of the updated policy.
        </p>
      </PolicySection>

      <PolicySection title="14. Contact us">
        <p>For privacy-related questions or to exercise your rights, contact:</p>
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
